import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import iziToast from 'izitoast';

import {
  addFileLink, deleteFile, getFileDownloadUrl, getStudentFiles, uploadStudentFile,
} from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { formatDate } from '../../utils/journalFormat';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';

const CATEGORIES = ['Сертификат', 'Документ слушателя', 'Прочее'];
const MAX_MB = 25;

const humanSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

/**
 * Файлы слушателя в журнале: сертификаты и сканы.
 *
 * Хранятся в S3, а не на диске сервера — диск занят на 89 %, и файлы контингента уже
 * съели две трети. Загрузка идёт напрямую в бакет по подписанной ссылке, минуя приложение.
 *
 * Заказчик отдельно называл вариант «ссылка на Яндекс.Диск» — поэтому рядом с загрузкой
 * есть добавление внешней ссылки.
 */
const StudentFiles = ({ studentId }) => {
  const { can } = useCurrentUser();
  const canEdit = can(PERMISSIONS.JOURNAL_MANAGE) || can(PERMISSIONS.JOURNAL_MARK_WRITE);
  const canDelete = can(PERMISSIONS.JOURNAL_MANAGE);

  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [category, setCategory] = useState('Сертификат');
  const [link, setLink] = useState({ url: '', name: '', issued_at: '' });

  const load = () => {
    setLoading(true);
    getStudentFiles(studentId)
      .then(setFiles)
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const pick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';   // чтобы повторный выбор того же файла сработал
    if (!file) return;

    if (file.size > MAX_MB * 1024 * 1024) {
      iziToast.error({ message: `Файл больше ${MAX_MB} МБ`, position: 'topRight' });
      return;
    }

    setUploading(true);
    uploadStudentFile(studentId, file, { category })
      .then(() => {
        iziToast.success({ message: 'Файл загружен', position: 'topRight' });
        load();
      })
      .catch((e) => iziToast.error({
        message: e.response?.data?.message || 'Не удалось загрузить файл',
        position: 'topRight',
      }))
      .finally(() => setUploading(false));
  };

  const download = (file) => {
    getFileDownloadUrl(file.id)
      .then((result) => window.open(result.url, '_blank', 'noopener'))
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  const remove = (file) => {
    deleteFile(file.id)
      .then(() => { iziToast.success({ message: 'Файл удалён', position: 'topRight' }); load(); })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  const saveLink = () => {
    addFileLink(studentId, { ...link, category })
      .then(() => {
        iziToast.success({ message: 'Ссылка добавлена', position: 'topRight' });
        setLinkOpen(false);
        setLink({ url: '', name: '', issued_at: '' });
        load();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <span className="journal-subtitle" style={{ margin: 0 }}>Документы и сертификаты</span>

        {canEdit && (
          <>
            <TextField
              select size="small" label="Категория" value={category}
              onChange={(event) => setCategory(event.target.value)} sx={{ width: 200 }}
            >
              {CATEGORIES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </TextField>

            <Button
              size="small" startIcon={<UploadFileIcon />} disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              Загрузить
            </Button>

            <Button size="small" startIcon={<LinkIcon />} onClick={() => setLinkOpen(true)}>
              Добавить ссылку
            </Button>

            <input
              ref={inputRef} type="file" hidden onChange={pick}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
            />
          </>
        )}
      </Stack>

      {uploading && <LinearProgress color="warning" sx={{ mb: 1 }} />}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={20} color="warning" /></Stack>
      ) : files.length === 0 ? (
        <div className="journal-muted">
          Файлов нет. Допустимы PDF, изображения и документы Word до {MAX_MB} МБ.
        </div>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Файл</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Размер</TableCell>
              <TableCell>Выдан</TableCell>
              <TableCell>Загрузил</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id} hover>
                <TableCell>
                  {file.original_name}
                  {file.comment && <div className="journal-muted">{file.comment}</div>}
                </TableCell>
                <TableCell><Chip size="small" label={file.category} /></TableCell>
                <TableCell>{file.is_link ? 'ссылка' : humanSize(file.size_bytes)}</TableCell>
                <TableCell>{formatDate(file.issued_at) || '—'}</TableCell>
                <TableCell className="journal-muted">{file.uploaded_by_name || '—'}</TableCell>
                <TableCell align="center">
                  <Tooltip title={file.is_link ? 'Открыть ссылку' : 'Скачать'}>
                    <IconButton size="small" onClick={() => download(file)}>
                      {file.is_link ? <OpenInNewIcon fontSize="small" /> : <DownloadIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>

                  {canDelete && (
                    <Tooltip title="Удалить">
                      <IconButton size="small" color="error" onClick={() => remove(file)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ссылка на документ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ссылка" value={link.url} placeholder="https://disk.yandex.ru/d/..."
              onChange={(event) => setLink((prev) => ({ ...prev, url: event.target.value }))}
            />
            <TextField
              label="Название" value={link.name}
              onChange={(event) => setLink((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              label="Дата выдачи" type="date" value={link.issued_at}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setLink((prev) => ({ ...prev, issued_at: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={saveLink} disabled={!link.url.trim()}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentFiles;
